import React from 'react';
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle } from './ui/dialog';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Textarea } from './ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from './ui/select';
import { Calendar, MapPin, Home, FileText, Clock } from 'lucide-react';

const DepartureDialog = ({ 
  open, 
  onOpenChange, 
  resident, 
  departData, 
  setDepartData, 
  onConfirm, 
  onCancel 
}) => {
  const departureReasons = [
    { value: 'departed', label: 'Voluntary Departure', icon: '🚪' },
    { value: 'eviction', label: 'Eviction', icon: '⚠️' },
    { value: 'completed_program', label: 'Completed Program', icon: '✅' },
    { value: 'transfer', label: 'Transfer to Another Facility', icon: '🔄' }
  ];

  const shelterTypes = [
    { value: 'emergency', label: 'Emergency Shelter' },
    { value: 'transitional', label: 'Transitional Housing' },
    { value: 'permanent', label: 'Permanent Housing' },
    { value: 'family', label: 'Family Shelter' },
    { value: 'rehabilitation', label: 'Rehabilitation Facility' },
    { value: 'other', label: 'Other' }
  ];

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle className="flex items-center space-x-2">
            <FileText className="h-5 w-5 text-indigo-600" />
            <span>Resident Departure Form</span>
          </DialogTitle>
          <DialogDescription>
            Complete departure information for {resident?.first_name} {resident?.last_name}
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-6">
          {/* Resident Info Summary */}
          <div className="p-4 bg-blue-50 border border-blue-200 rounded-lg">
            <h4 className="font-semibold text-blue-900 mb-2">Resident Information</h4>
            <div className="grid grid-cols-2 gap-4 text-sm">
              <div>
                <span className="text-blue-700">Name:</span> {resident?.first_name} {resident?.last_name}
              </div>
              <div>
                <span className="text-blue-700">Intake Date:</span> {resident?.intake_date ? new Date(resident.intake_date).toLocaleDateString() : 'N/A'}
              </div>
              <div>
                <span className="text-blue-700">Stay Length:</span> {resident?.stay_length_days || 0} days
              </div>
              <div>
                <span className="text-blue-700">Room:</span> {resident?.room_id || 'Not Assigned'}
              </div>
            </div>
          </div>

          {/* Departure Details */}
          <div className="space-y-4">
            <div>
              <label className="text-sm font-medium text-gray-700 flex items-center space-x-2 mb-2">
                <Clock className="h-4 w-4" />
                <span>Departure Reason *</span>
              </label>
              <Select
                value={departData.departure_reason}
                onValueChange={(value) => setDepartData({...departData, departure_reason: value})}
              >
                <SelectTrigger>
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  {departureReasons.map((reason) => (
                    <SelectItem key={reason.value} value={reason.value}>
                      <div className="flex items-center space-x-2">
                        <span>{reason.icon}</span>
                        <span>{reason.label}</span>
                      </div>
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            <div>
              <label className="text-sm font-medium text-gray-700 flex items-center space-x-2 mb-2">
                <MapPin className="h-4 w-4" />
                <span>Forwarding Address</span>
              </label>
              <Textarea
                value={departData.forwarding_address}
                onChange={(e) => setDepartData({...departData, forwarding_address: e.target.value})}
                placeholder="Enter forwarding address if known..."
                rows={3}
              />
            </div>

            <div>
              <label className="text-sm font-medium text-gray-700 flex items-center space-x-2 mb-2">
                <Home className="h-4 w-4" />
                <span>Destination Shelter Type</span>
              </label>
              <Select
                value={departData.destination_shelter_type}
                onValueChange={(value) => setDepartData({...departData, destination_shelter_type: value})}
              >
                <SelectTrigger>
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  {shelterTypes.map((type) => (
                    <SelectItem key={type.value} value={type.value}>
                      {type.label}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            <div>
              <label className="text-sm font-medium text-gray-700 flex items-center space-x-2 mb-2">
                <FileText className="h-4 w-4" />
                <span>Departure Notes</span>
              </label>
              <Textarea
                value={departData.departure_notes}
                onChange={(e) => setDepartData({...departData, departure_notes: e.target.value})}
                placeholder="Additional notes about the departure..."
                rows={4}
              />
            </div>
          </div>

          {/* 30-Day Rule Notice */}
          <div className="p-4 bg-yellow-50 border border-yellow-200 rounded-lg">
            <div className="flex items-start space-x-2">
              <Calendar className="h-5 w-5 text-yellow-600 mt-0.5" />
              <div>
                <h4 className="font-semibold text-yellow-900">30-Day Re-entry Policy</h4>
                <p className="text-sm text-yellow-700 mt-1">
                  This resident will not be eligible for readmission for 30 days from the departure date. 
                  Readmission will be available starting {new Date(Date.now() + 30 * 24 * 60 * 60 * 1000).toLocaleDateString()}.
                </p>
              </div>
            </div>
          </div>

          {/* Action Buttons */}
          <div className="flex justify-end space-x-3 pt-4 border-t">
            <Button variant="outline" onClick={onCancel}>
              Cancel
            </Button>
            <Button 
              onClick={onConfirm}
              className="bg-orange-600 hover:bg-orange-700"
            >
              Complete Departure
            </Button>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
};

export default DepartureDialog;